import React, { useState } from "react";
import AddNewPatient from "../addPatients";
import { ModalType } from "../../../../shared/utils/enums";
import MainTable from "../../../../components/mainTable";
import { useDispatch } from "react-redux";
import { showModal } from "../../../../connected-components/modal/reducer";
import { screens } from "../../../../config/routes";
import { useNavigate } from "react-router-dom";
import { fetchPatients } from "./actions";
import { Patient } from "../../../../interfaces/api/patients";
import patientsApi from "../../../../api/patients";
import { toastService } from "../../../../shared/utils/toast";
import {
  showLoading,
  hideLoading,
} from "../../../../connected-components/loader/reducer";
import { getPatientsTableColumns } from "../../../../shared/tableColumns/patientsColumns";

const PatientsList = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [patients, setPatients] = useState<Patient[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [isLoading, setIsLoading] = useState(false);

  React.useEffect(() => {
    const loadPatients = async () => {
      setIsLoading(true);
      const res = await fetchPatients(currentPage + 1, pageSize);
      if (res && res.result === "success" && res.content && res.content.data) {
        setPatients(res.content.data);
      }
      setIsLoading(false);
    };
    loadPatients();
    // eslint-disable-next-line
  }, [currentPage, pageSize]);

  const handleRowPress = (patient: Patient) => {
    navigate(screens.patientDetails?.replace(":id", String(patient.id)));
  };

  const handlePageChange = ({
    pageIndex,
    pageSize: newPageSize,
  }: {
    pageIndex: number;
    pageSize: number;
  }) => {
    setCurrentPage(pageIndex);
    setPageSize(newPageSize);
  };

  const handleAddNewPatient = () => {
    dispatch(
      showModal({
        type: ModalType.COMPONENT,
        title: "Add New Patient",
        component: <AddNewPatient />,
        onClose: async () => {
          setIsLoading(true);
          const res = await fetchPatients(currentPage + 1, pageSize);
          if (
            res &&
            res.result === "success" &&
            res.content &&
            res.content.data
          ) {
            setPatients(res.content.data);
          }
          setIsLoading(false);
        },
      }),
    );
  };

  const handleDeletePatient = (patient: Patient) => {
    dispatch(
      showModal({
        type: ModalType.QUESTION,
        title: "Delete Patient",
        message:
          "Are you sure you want to delete this patient? This action cannot be undone.",
        onConfirm: async () => {
          try {
            dispatch(showLoading());
            await patientsApi.deletePatient(String(patient.id));
            toastService.showSuccessToast("Patient deleted successfully");
            setPatients((prev) => prev.filter((p) => p.id !== patient.id));
          } catch (err) {
            toastService.showErrorToast("Failed to delete patient");
          } finally {
            dispatch(hideLoading());
          }
        },
        onCancel: () => {},
      }),
    );
  };

  const columns = getPatientsTableColumns({ onDelete: handleDeletePatient });

  return (
    <div className="py-4">
      <MainTable
        columns={columns}
        data={patients}
        isLoading={isLoading}
        tableClass="min-w-full divide-y divide-gray-200 dark:divide-gray-700"
        onRowPress={handleRowPress}
        showPagination={true}
        totalData={patients.length}
        pageSize={pageSize}
        currentPage={currentPage}
        onPageChanged={handlePageChange}
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        showSearch={true}
        addButtonLabel="Add New Patient"
        onAddNewItemPress={handleAddNewPatient}
      />
    </div>
  );
};

export default PatientsList;
